import { Component, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SetupWizardUser, TextUtils } from '@mira-avant/mtlibs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
	BEFORE_AFTER_STATUS_CHANGED_COLOR,
	BEFORE_AFTER_STATUS_NEW_COLOR,
	BEFORE_AFTER_STATUS_REMOVED_COLOR,
	BEFORE_AFTER_STATUS_UNCHANGED_COLOR,
	BEFORE_AFTER_STATUS_UPSERT_COLOR,
} from '../../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SetupWizard } from '../../../../../../../../services/setup-wizard';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
	selector: 'app-setup-wizard-user-locations-status',
	imports: [NzTagModule, NzTooltipModule, NzSpinModule, NzIconModule, NzButtonModule],
	standalone: true,
	templateUrl: './setup-wizard-user-locations-status.html',
	styleUrl: './setup-wizard-user-locations-status.scss',
})
export class SetupWizardUserLocationsStatus {
	enableEditButton = input<boolean>(true);
	user = input<SetupWizardUser>();
	existing = signal<number>(0);
	added = signal<number>(0);
	removed = signal<number>(0);
	loading = signal<boolean>(true);
	onEdit = output<SetupWizardUser>();
	private sub?: Subscription;
	public BEFORE_AFTER_STATUS_UPSERT_COLOR = BEFORE_AFTER_STATUS_UPSERT_COLOR;
	public BEFORE_AFTER_STATUS_NEW_COLOR = BEFORE_AFTER_STATUS_NEW_COLOR;
	public BEFORE_AFTER_STATUS_CHANGED_COLOR = BEFORE_AFTER_STATUS_CHANGED_COLOR;
	public BEFORE_AFTER_STATUS_UNCHANGED_COLOR = BEFORE_AFTER_STATUS_UNCHANGED_COLOR;
	public BEFORE_AFTER_STATUS_REMOVED_COLOR = BEFORE_AFTER_STATUS_REMOVED_COLOR;

	constructor(private setupWizard: SetupWizard) {
		const user$ = toObservable(this.user).pipe(distinctUntilChanged());

		this.sub = combineLatest([this.setupWizard.preview$, user$]).subscribe(([preview, user]) => {
			if (!user) {
				this.resetCounts();
				return;
			}

			const userBeforeAfter = user.after ?? user.before;

			if (!userBeforeAfter?.user_uuid) {
				this.resetCounts();
				return;
			}

			let existing = 0;
			let added = 0;
			let removed = 0;

			for (const link of preview.payload.schoolAdminsToLocations) {
				const beforeAfter = link.after ?? link.before;

				if (!beforeAfter) {
					throw new Error('UserToLocation link has no before or after state');
				}

				if (
					TextUtils.isNonEmptyString(beforeAfter.user_uuid) &&
					beforeAfter.user_uuid !== userBeforeAfter.user_uuid
				) {
					continue;
				}

				if (!link.before && link.after) {
					added++;
				} else if (link.before && !link.after) {
					existing++;
				} else if (link.before && link.after && link.before.active !== link.after.active) {
					removed++;
				}
			}

			this.existing.set(existing);
			this.added.set(added);
			this.removed.set(removed);
			this.loading.set(false);
		});
	}

	editUser(user: SetupWizardUser) {
		this.onEdit.emit(user);
	}

	private resetCounts() {
		this.existing.set(0);
		this.added.set(0);
		this.removed.set(0);
		this.loading.set(false);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
